import React, { useState, useEffect } from 'react';
import { Package, Truck, Search, Plus, X, Calendar, User, FileText, IndianRupee, Layers, Pencil, Trash2 } from 'lucide-react';
import SummaryApi from '../../common/index';
import { toast } from 'react-toastify';
import fetchWithAuth from '../../common/fetchWithAuth';

export default function ParcelManagement() {
    const [parcels, setParcels] = useState([]);
    const [loading, setLoading] = useState(false);

    const [showForm, setShowForm] = useState(false);
    const [search, setSearch] = useState("");
    const [formData, setFormData] = useState({
        city: "",
        quantity: "",
        date: new Date().toISOString().split('T')[0],
        lrNo: "",
        charge: "",
    });
    const [editId, setEditId] = useState(null);

    const handleSearch = (e) => setSearch(e.target.value);

    const filteredParcels = parcels.filter(p =>
        p.city?.toLowerCase().includes(search.toLowerCase()) ||
        p.lrNo?.toLowerCase().includes(search.toLowerCase())
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const fetchParcels = async () => {
        try {
            setLoading(true);
            const response = await fetchWithAuth(SummaryApi.getAllParcels.url, {
                method: SummaryApi.getAllParcels.method,
            })
            const result = await response.json();
            if (result.success) {
                setParcels(result.data);
            } else {
                toast.error(result.message);
            }
        } catch (error) {
            console.log(error);
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }

    const executeDelete = async (id) => {
        try {
            const response = await fetchWithAuth(SummaryApi.deleteParcel.url(id), {
                method: SummaryApi.deleteParcel.method,
            });
            const result = await response.json();
            if (result.success) {
                toast.success(result.message);
                fetchParcels();
            } else {
                toast.error(result.message);
            }
        } catch (error) {
            console.log(error);
            toast.error("Error deleting parcel");
        }
    }

    const handleDelete = (id) => {
        toast(({ closeToast }) => (
            <div className="flex flex-col gap-2">
                <p className="text-gray-800 font-medium text-sm">Are you sure you want to delete this parcel?</p>
                <div className="flex gap-2 justify-end">
                    <button
                        onClick={closeToast}
                        className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 border border-gray-200 rounded-md transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            executeDelete(id);
                            closeToast();
                        }}
                        className="px-3 py-1.5 text-xs bg-red-500 text-white hover:bg-red-600 rounded-md transition-colors font-medium shadow-sm"
                    >
                        Delete
                    </button>
                </div>
            </div>
        ), {
            position: "top-right",
            autoClose: false,
            closeOnClick: false,
            draggable: false,
            closeButton: false,
            className: "p-2 rounded-xl shadow-xl border border-gray-100"
        });
    }

    const handleEdit = (parcel) => {
        setFormData({
            city: parcel.city,
            quantity: parcel.quantity,
            date: new Date(parcel.date).toISOString().split('T')[0],
            lrNo: parcel.lrNo,
            charge: parcel.charge,
        });
        setEditId(parcel._id);
        setShowForm(true);
    }

    useEffect(() => {
        fetchParcels();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const newParcel = {
                ...formData,
                charge: Number(formData.charge),
                quantity: Number(formData.quantity)
            };

            const response = editId
                ? await fetchWithAuth(SummaryApi.updateParcel.url(editId), {
                    method: SummaryApi.updateParcel.method,
                    body: JSON.stringify(newParcel)
                })
                : await fetchWithAuth(SummaryApi.addParcel.url, {
                    method: SummaryApi.addParcel.method,
                    body: JSON.stringify(newParcel)
                })

            const result = await response.json();
            if (result.success) {
                toast.success(result.message);
                fetchParcels();
                setShowForm(false);
                setEditId(null);
                setFormData({
                    city: "",
                    quantity: "",
                    date: new Date().toISOString().split('T')[0],
                    lrNo: "",
                    charge: "",
                });
            } else {
                toast.error(result.message);
            }
        } catch (err) {
            console.log(err.message);
            toast.error(err.message);
        }
    };

    return (
        <div className="w-full pb-10">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                    <span className="p-2 bg-indigo-100 rounded-xl text-indigo-600">
                        <Package size={32} />
                    </span>
                    Parcel Management
                </h1>
                <p className="text-gray-500 text-sm mt-1 ml-1">
                    Daily Parcel Ledger
                </p>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
                <div className="relative w-full md:w-96">
                    <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
                    <input
                        placeholder="Search by City or LR No..."
                        value={search}
                        onChange={handleSearch}
                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                    />
                </div>
                <button
                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-medium transition-all shadow-sm"
                    onClick={() => {
                        setEditId(null);
                        setFormData({
                            city: "",
                            quantity: "",
                            date: new Date().toISOString().split('T')[0],
                            lrNo: "",
                            charge: "",
                        });
                        setShowForm(true);
                    }}
                >
                    <Plus size={20} />
                    Add Parcel
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider font-semibold border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4">SR.NO</th>
                                <th className="px-6 py-4">Date</th>
                                <th className="px-6 py-4">LR No</th>
                                <th className="px-6 py-4">Quantity</th>
                                <th className="px-6 py-4">City</th>
                                <th className="px-6 py-4 text-right">Amount (₹)</th>
                                <th className="px-6 py-4 text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                                        Loading parcels...
                                    </td>
                                </tr>
                            ) : filteredParcels.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-8 text-center text-gray-400 italic">
                                        No parcels found.
                                    </td>
                                </tr>
                            ) : (
                                filteredParcels.map((parcel, index) => (
                                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 text-gray-500 text-sm">{index + 1}</td>
                                        <td className="px-6 py-4 text-gray-800 font-medium">
                                            {new Date(parcel.date).toLocaleDateString('en-GB')}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 font-mono text-sm">
                                            {parcel.lrNo || "-"}
                                        </td>
                                        <td className="px-6 py-4 text-gray-700">
                                            {parcel.quantity}
                                        </td>
                                        <td className="px-6 py-4 text-gray-700 capitalize">
                                            {parcel.city}
                                        </td>
                                        <td className="px-6 py-4 text-right font-bold text-gray-900">
                                            {parcel.charge.toLocaleString('en-IN')}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() => handleEdit(parcel)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="Edit Parcel"
                                                >
                                                    <Pencil size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(parcel._id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Delete Parcel"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
                    <div className="w-full max-w-md bg-white rounded-2xl shadow-xl animate-in fade-in zoom-in-95">
                        <div className="p-5 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-800">
                                {editId ? "Edit Parcel Entry" : "Add Parcel Entry"}
                            </h3>
                            <button
                                onClick={() => setShowForm(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">

                            {/* Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                <input
                                    type="date"
                                    required
                                    name="date"
                                    value={formData.date}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
                                />
                            </div>

                            {/* LR No */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">LR No</label>
                                <input
                                    name="lrNo"
                                    value={formData.lrNo}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
                                    placeholder="Enter LR Number"
                                />
                            </div>

                            {/* Quantity */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                                <input
                                    type="number"
                                    required
                                    name="quantity"
                                    value={formData.quantity}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
                                    placeholder="e.g. 5"
                                />
                            </div>

                            {/* City */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                <input
                                    required
                                    name="city"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
                                    placeholder="e.g. Mumbai"
                                />
                            </div>

                            {/* Amount */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                                <input
                                    type="number"
                                    required
                                    name="charge"
                                    value={formData.charge}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
                                    placeholder="0"
                                />
                            </div>

                            <div className="pt-4 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowForm(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-md transition-colors font-medium"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
